/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { sessionManager } from "@/broadcastChannel/sessionManager";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { langs } from "@/constants/constants";
import usePostData from "@/hooks/usePostData";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronDown,
  Eye,
  EyeOff,
  KeyRound,
  LogOut,
  Mail,
  User2,
  UserPen,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { LoadingSpinner } from "../ui/loadingSpinner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useToast } from "../ui/use-toast";
import "/node_modules/flag-icons/css/flag-icons.min.css";

//**************** header submit **************** //
const Navigation = ({
  hideCallForProjectHeaderActions = false,
}: {
  hideCallForProjectHeaderActions?: boolean;
}) => {
  // const { isAuthenticated, logout } = useAuth();
  const { user, logout, isAuthenticated, setAuthState } = useAuth();

  const [openEditProfil, setOpenEditProfil] = useState(false);

  const [loadingEditProfil, setLoadingEditProfil] = useState(false);

  const { postData } = usePostData<any>();
  const hidePostActions = useHidePostActions();

  const { i18n } = useTranslation();
  const { t } = useTranslation("general");

  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isCallForProject, setIsCallForProject] = useState(
    sessionStorage.getItem("subcontractorId") ? true : false,
  );
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [nameOrg, setNameOrg] = useState("");
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [errorOrg, setErrorOrg] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const navigate = useNavigate();
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(1);
  const [minutes, setMinutes] = useState(0);

  // Conversion en minutes pour le backend
  const durationInMinutes = days * 1440 + hours * 60 + minutes;
  const isInvalidDuration = durationInMinutes <= 0;

  const passwordComplexityRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/;

  const formSchema = z
    .object({
      lastname: z.string().min(2, {
        message: t("Le nom de famille doit contenir au moins 2 caractères."),
      }),
      firstname: z.string().min(2, {
        message: t("Le prénom doit contenir au moins 2 caractères."),
      }),
      structureName: z.string().min(2, {
        message: t(
          "Le nom de la structure doit contenir au moins 2 caractères.",
        ),
      }),
      password: z
        .string()
        .optional()
        .refine((val) => !val || val.length >= 8, {
          message: t("Le mot de passe doit contenir au moins 8 caractères"),
        })
        .refine((val) => !val || passwordComplexityRegex.test(val), {
          message: t(
            "Le mot de passe doit contenir au moins  une majuscule, une minuscule, un chiffre et un caractère spécial",
          ),
        }),
      confirmPassword: z.string().optional(),
      siret: z.string().regex(/^\d{14}$/, {
        message: t("Le SIRET doit contenir exactement 14 chiffres."),
      }),
      siren: z.string().regex(/^\d{9}$/, {
        message: t("Le SIREN doit contenir exactement 9 chiffres."),
      }),
      numeroVoie: z.string().min(1, {
        message: t("Le numéro de voie est requis."),
      }),
      rue: z.string().min(2, {
        message: t("Le nom de la rue doit contenir au moins 2 caractères."),
      }),
      codePostal: z.string().regex(/^\d{3,5}$/, {
        message: t("Le code postal doit contenir 3 à 5 chiffres."),
      }),
      ville: z.string().min(2, {
        message: t("La ville doit contenir au moins 2 caractères."),
      }),
      pays: z.string().min(2, {
        message: t("Le pays doit contenir au moins 2 caractères."),
      }),
    })
    .superRefine((data, ctx) => {
      if (data.password && data.password !== data.confirmPassword) {
        ctx.addIssue({
          path: ["confirmPassword"],
          code: z.ZodIssueCode.custom,
          message: t("Les mots de passe ne correspondent pas."),
        });
      }
    });

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: user?.firstname || "",
      lastname: user?.lastname || "",
      structureName: user?.structureName || "",
      password: "",
      confirmPassword: "",
      siret: user?.Organisation?.siret || "",
      siren: user?.Organisation?.siren || "",
      numeroVoie: user?.Organisation?.numeroVoie || "",
      rue: user?.Organisation?.rue || "",
      codePostal: user?.Organisation?.codePostal || "",
      ville: user?.Organisation?.ville || "",
      pays: user?.Organisation?.pays || "",
    },
  });

  useEffect(() => {
    if (user) {
      form.reset({
        firstname: user?.firstname,
        lastname: user?.lastname,
        structureName: user?.structureName,
        password: "",
        confirmPassword: "",
        siret: user?.Organisation?.siret || "",
        siren: user?.Organisation?.siren || "",
        numeroVoie: user?.Organisation?.numRue || "",
        rue: user?.Organisation?.rue || "",
        codePostal: user?.Organisation?.codePostale || "",
        ville: user?.Organisation?.ville || "",
        pays: user?.Organisation?.pays || "",
      });
      setNameOrg(user?.structureName);
    }
  }, [user, form]);

  const fetchResults = async (type = 1) => {
    setLoadingSearch(true);

    let token = localStorage.getItem("jwtToken");
    if (token === null) {
      token = sessionStorage.getItem("jwtToken");
    }
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_ENDPOINT
        }/financer/search/external?s=${query}&type=1}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await response.json();
      setResults(data.list || []);
    } catch (error) {
      console.error("Erreur lors de la récupération des résultats :", error);
      setResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  useEffect(() => {
    if (query === undefined || query.length < 3) {
      setResults([]);
      return;
    }

    if (query.length > 0) {
      setSelectedOrganisation(null);
    }

    const delayDebounceFn = setTimeout(() => {
      fetchResults(1);
    }, 300); // Attente de 300ms pour éviter les appels API excessifs

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  const handleEditProfil = async (data: z.infer<typeof formSchema>) => {
    setLoadingEditProfil(true);
    const newData = {
      firstname: data.firstname,
      lastname: data.lastname,
      structureName: data.structureName,
      password: data.password,
      email: user?.email,
      confirmPassword: data.confirmPassword,
      siret: data.siret,
      siren: data.siren,
      numeroVoie: data.numeroVoie,
      rue: data.rue,
      codePostal: data.codePostal,
      ville: data.ville,
      pays: data.pays,
    };

    const res = await postData({
      url: `${import.meta.env.VITE_IP_API}/api/profil`,
      data: { ...newData },
    });
    if (res === null) {
      setLoadingEditProfil(false);
      return;
    }

    setAuthState({ user: res, loading: false, error: null });

    setLoadingEditProfil(false);

    toast({
      title: t("Votre profil a bien été modifié."),
      duration: 1000,
    });
    window.location.reload();
    setOpenEditProfil(false);
  };

  const saveLanguageParameter = async (value) => {
    const res = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/parameter/save`,
      data: {
        user: user?.id,
        project: null,
        name: "lang",
        value: value,
      },
    });
    if (res === null) {
      return;
    }

    // setAuthState({ user: res, loading: false, error: null });

    // setLoadingEditProfil(false);

    toast({
      title: t("Choix de langue enregistré"),
      duration: 1000,
    });
  };

  function logoutCall() {
    const slugName =
      sessionStorage.getItem("slugName") ?? localStorage.getItem("slugName");
    const subcontractorId =
      sessionStorage.getItem("subcontractorId") ?? localStorage.getItem("link");

    sessionStorage.removeItem("jwtToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("subcontractorId");
    sessionStorage.removeItem("dataCall");
    sessionStorage.removeItem("slugName");
    sessionStorage.removeItem("subcontractorId");
    // sessionStorage.clear();
    // localStorage.clear();
    sessionManager.logout();
    // navigate(`/authentification-cfp/${subcontractorId}/${slugName}`);
    window.location.href = `/authentification-cfp/${subcontractorId}/${slugName}`;
  }

  return (
    <div className="container mb-8">
      <nav className="relative flex h-16 items-center border-b md:h-24">
        <ul className="flex w-full items-center">
          <li>
            <Link to="/dashboard">
              <img
                src="/logo.svg"
                alt="logo verdeo"
                width={138}
                className="w-24 md:w-[138px]"
              />
            </Link>
          </li>
          {!hideCallForProjectHeaderActions && (
            <>
          <li className="ml-auto mr-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="cursor-pointer">
                <div className="flex items-center">
                  {i18n === undefined ||
                  i18n === null ||
                  i18n.language === undefined ||
                  i18n.language === null
                    ? "FR"
                    : i18n.language.split("-")[0].toUpperCase()}
                  {/* <span className={"fi fi-"+(i18n.language === "en" ? "gb" : (i18n.language === "fr-FR" ? "fr" : i18n.language))} style={{
                    width: '45px',
                    height: '25px',
                    backgroundSize: 'contain',
                  }}></span> */}
                  <ChevronDown className="size-4 opacity-50" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-[10px]">
                <DropdownMenuGroup className="w-[50px]">
                  {langs.map((lang, key) => (
                    <DropdownMenuItem
                      className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                      onClick={() => {
                        i18n.language !== lang && i18n.changeLanguage(lang);
                        saveLanguageParameter(lang);
                      }}
                      key={key}
                    >
                      {lang.toUpperCase()}
                      {/* <span className={"fi fi-"+(lang === "en" ? "gb" : lang)+(i18n.language === lang ? " opacity-70 cursor-not-allowed" : "")} style={{
                                width: '45px',
                                height: '25px',
                                backgroundSize: 'contain',
                              }}></span> */}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
          <li className="mr-4 text-right">
            <a
              href="https://verdeo.fr/contact/"
              className="group relative flex size-[42px] items-center justify-center rounded-full border border-foreground md:size-max md:border-0"
            >
              <span className="hidden text-xs md:block md:text-base">
                {t("Contactez-nous")}!{" "}
              </span>
              <span className="absolute left-0 top-full hidden h-px w-0 bg-foreground transition-all group-hover:w-full md:block"></span>
              <Mail className="block stroke-1 md:hidden" />
            </a>
          </li>
          {isAuthenticated && isCallForProject && (
            <li>
              <Button
                variant="outline"
                className="rounded-[4rem]"
                onClick={() => {
                  logoutCall();
                }}
              >
                {t("Se déconnecter")}
              </Button>
            </li>
          )}
            </>
          )}
          {!isAuthenticated && (
            <li>
              <Link
                to="/login"
                className="flex items-center justify-center rounded-[4rem] border-2 border-foreground bg-black p-2 transition-all hover:bg-foreground hover:text-background md:bg-background md:px-4"
              >
                <span className="hidden md:block">{t("Connexion")}</span>
                <User2 className="block stroke-1 text-background md:hidden" />
              </Link>
            </li>
          )}
          {isAuthenticated && !isCallForProject && (
            <li className="pl-5">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    disabled={!user?.isVerified}
                    className="flex cursor-pointer items-center gap-1 text-foreground bg-transparent"
                  >
                    {user ? (
                      <>
                        <User2 />
                        {user?.firstname} {user?.lastname}
                      </>
                    ) : (
                      <LoadingSpinner />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-max z-10">
                  <div className="mb-2 flex items-center gap-1">
                    <User2 className="text-muted-foreground" />
                    <div>
                      <p className="m-0 text-xs text-muted-foreground">
                        {t("Connecté en tant que ")}
                      </p>
                      <p className="m-0 text-muted-foreground">
                        {user?.firstname} {user?.lastname}
                      </p>
                    </div>
                  </div>
                  <hr />
                  <AlertDialog
                    open={openEditProfil}
                    onOpenChange={setOpenEditProfil}
                  >
                    <AlertDialogTrigger asChild>
                      <button
                        disabled={
                          localStorage.getItem("isImpersonating") === "true"
                        }
                        className="flex cursor-pointer items-center gap-2 py-2 text-muted-foreground transition-all hover:text-destructive disabled:opacity-50"
                      >
                        <UserPen size={20} />
                        {t("Profil")}
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t("Modifier mon profil")}
                        </AlertDialogTitle>
                      </AlertDialogHeader>
                      <Form {...form}>
                        <form
                          onSubmit={form.handleSubmit(handleEditProfil)}
                          className="space-y-8 scroll-auto"
                        >
                          <FormField
                            control={form.control}
                            name="lastname"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("Nom")}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t("Nom")}
                                    {...field}
                                    autoComplete="off"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="firstname"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("Prénom")}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t("Prénom")}
                                    {...field}
                                    autoComplete="off"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* <FormField
                            control={form.control}
                            name="structureName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("Nom de la structure")}
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t("Nom de la structure")}
                                    autoComplete="off"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          /> */}

                          <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("Mot de passe")}</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <Input
                                      placeholder={t("Mot de passe")}
                                      autoComplete="new-password"
                                      {...field}
                                      type={showPassword ? "text" : "password"}
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setShowPassword(!showPassword);
                                      }}
                                      className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                                    >
                                      {!showPassword ? <EyeOff /> : <Eye />}
                                    </button>
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("Confirmation mot de passe")}
                                </FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <Input
                                      placeholder={t(
                                        "Confirmation mot de passe",
                                      )}
                                      {...field}
                                      type={
                                        showPasswordConfirm
                                          ? "text"
                                          : "password"
                                      }
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setShowPasswordConfirm(
                                          !showPasswordConfirm,
                                        );
                                      }}
                                      className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                                    >
                                      {!showPasswordConfirm ? (
                                        <EyeOff />
                                      ) : (
                                        <Eye />
                                      )}
                                    </button>
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Organisation name */}
                          <FormField
                            control={form.control}
                            name="structureName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("Nom de la structure")}
                                </FormLabel>
                                <FormControl>
                                  {/* <Input placeholder={t("Nom de la structure")} autoComplete="off" {...field} /> */}
                                  <div>
                                    <Input
                                      type="text"
                                      style={{
                                        color: "#878787",
                                      }}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setNameOrg(e.target.value);
                                        setQuery(e.target.value);
                                        if (value.trim() === "") {
                                          setErrorOrg(
                                            t(
                                              "Le nom de l'organisation doit contenir aumoins 3 caracteres",
                                            ),
                                          );
                                          setIsSaveDisabled(true);
                                        } else {
                                          setErrorOrg("");
                                          setIsSaveDisabled(false);
                                        }
                                      }}
                                      className="text-muted-foreground"
                                      value={nameOrg ?? user?.structureName}
                                      // {...field}
                                    />
                                    {loadingSearch && (
                                      <p className="absolute mt-2 text-sm rounded-md border border-gray-300 bg-white shadow-lg px-4 py-2 text-gray-500">
                                        Chargement...
                                      </p>
                                    )}
                                    {!loadingSearch &&
                                      query &&
                                      results.length > 0 && (
                                        <ul className="absolute mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                                          {results.map((result, index) => (
                                            <li
                                              key={index}
                                              className="flex cursor-pointer gap-2 border-b px-4 py-2 last:border-b-0 hover:bg-blue-100"
                                              onClick={() => {
                                                setSelectedOrganisation(result);
                                                // console.log("result", result);
                                                setNameOrg(
                                                  result.nom_entreprise,
                                                );
                                                form.setValue(
                                                  "structureName",
                                                  result.nom_entreprise,
                                                  { shouldValidate: true },
                                                );
                                                form.setValue(
                                                  "siret",
                                                  result.siege.siret,
                                                  { shouldValidate: true },
                                                );
                                                form.setValue(
                                                  "siren",
                                                  result.siren,
                                                  { shouldValidate: true },
                                                );
                                                form.setValue(
                                                  "numeroVoie",
                                                  result.siege?.numero_voie
                                                    ? String(
                                                        result.siege
                                                          .numero_voie,
                                                      )
                                                    : "",
                                                  { shouldValidate: true },
                                                );
                                                form.setValue(
                                                  "rue",
                                                  result.siege.type_voie +
                                                    " " +
                                                    result.siege.libelle_voie,
                                                  { shouldValidate: true },
                                                );
                                                form.setValue(
                                                  "codePostal",
                                                  result.siege.code_postal,
                                                  { shouldValidate: true },
                                                );
                                                form.setValue(
                                                  "ville",
                                                  result.siege.ville,
                                                  { shouldValidate: true },
                                                );
                                                form.setValue(
                                                  "pays",
                                                  result.siege.pays,
                                                  { shouldValidate: true },
                                                );
                                                setQuery("");
                                                setResults([]);
                                              }}
                                            >
                                              <span>
                                                {result.nom_entreprise}
                                              </span>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                  </div>
                                </FormControl>
                                <FormMessage />
                                {/* Message d'erreur */}
                                {errorOrg && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errorOrg}
                                  </p>
                                )}
                              </FormItem>
                            )}
                          />

                          {/* SIRET et SIREN */}
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="siret"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("SIRET")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("SIRET")}
                                      autoComplete="off"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="siren"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("SIREN")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("SIREN")}
                                      autoComplete="off"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* Adresse */}
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="numeroVoie"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Numéro de voie")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("Numéro de voie")}
                                      autoComplete="off"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="rue"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Rue")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("Rue")}
                                      autoComplete="off"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <FormField
                              control={form.control}
                              name="codePostal"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Code postal")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("Code postal")}
                                      autoComplete="off"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="ville"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Ville")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("Ville")}
                                      autoComplete="off"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="pays"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Pays")}</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder={t("Pays")}
                                      autoComplete="off"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <AlertDialogFooter>
                            <AlertDialogCancel>
                              {t("Annuler")}
                            </AlertDialogCancel>
                            {!hidePostActions && (
                              <Button
                                type="submit"
                                disabled={loadingEditProfil}
                              >
                                {t("Sauvegarder")}
                              </Button>
                            )}
                          </AlertDialogFooter>
                        </form>
                      </Form>
                      {/* <div className="mt-3">
                        <div className="space-y-2">
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("Nom")}
                          </label>
                          <input
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            type="text"
                            name="name"
                            value={lastname}
                            onChange={(e) => setLastname(e.target.value)}
                          />
                        </div>
                        <div className="mt-4 space-y-2">
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("Prénom")}
                          </label>
                          <input
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            type="text"
                            name="firstname"
                            value={firstname}
                            onChange={(e) => setFirstname(e.target.value)}
                          />
                        </div>
                        <div className="mt-4 space-y-2">
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("Nom de la structure")}
                          </label>
                          <input
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            type="text"
                            name="structureName"
                            value={structureName}
                            onChange={(e) => setStructureName(e.target.value)}
                          />
                        </div>
                        <div className="mt-4 space-y-2">
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("Email")}
                          </label>
                          <input
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            type="email"
                            name="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>
                        <div className="mt-4 space-y-2">
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("Mot de passe")}
                          </label>
                          <input
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            type="password"
                            name="password"
                            autoComplete="new-password"
                            autoCorrect="off"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                        </div>
                        <div className="mt-4 space-y-2">
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("Confirmation mot de passe")}
                          </label>
                          <input
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            type="password"
                            name="confirmPassword"
                            autoComplete="new-password"
                            autoCorrect="off"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                        </div>
                      </div> */}
                    </AlertDialogContent>
                  </AlertDialog>
                  <hr />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        disabled={user?.giveAccess}
                        className="flex cursor-pointer items-center gap-2 py-2 text-muted-foreground transition-all hover:text-destructive disabled:opacity-50"
                      >
                        <KeyRound size={20} />
                        Donner accès à l’admin
                      </button>
                    </AlertDialogTrigger>

                    <AlertDialogContent className="sm:max-w-[600px]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-2xl">
                          Confirmer l’accès
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Vous êtes sur le point de donner un accès temporaire à
                          un Responsable Verdeo. Il pourra accéder à votre
                          tableau de bord pendant la durée choisie.
                        </AlertDialogDescription>
                      </AlertDialogHeader>

                      {/* Inputs jours / heures / minutes */}
                      <div className="space-y-3 py-4">
                        <label className="text-sm font-medium">
                          Durée de l’accès
                        </label>

                        <div className="flex gap-3">
                          <div className="flex flex-row gap-2 items-center">
                            <Input
                              type="number"
                              min={0}
                              value={days}
                              onChange={(e) => setDays(Number(e.target.value))}
                              className="w-20"
                            />
                            <p className="text-lg text-muted-foreground text-center m-0">
                              j
                            </p>
                          </div>

                          <div className="flex flex-row gap-2 items-center">
                            <Input
                              type="number"
                              min={0}
                              max={23}
                              value={hours}
                              onChange={(e) => setHours(Number(e.target.value))}
                              className="w-20"
                            />
                            <p className="text-lg text-muted-foreground text-center m-0">
                              h
                            </p>
                          </div>

                          <div className="flex flex-row gap-2 items-center">
                            <Input
                              type="number"
                              min={0}
                              max={59}
                              step={5}
                              value={minutes}
                              onChange={(e) =>
                                setMinutes(Number(e.target.value))
                              }
                              className="w-20"
                            />
                            <p className="text-lg text-muted-foreground text-center m-0">
                              min
                            </p>
                          </div>
                        </div>

                        {durationInMinutes >= 240 && (
                          <p className="text-xs text-orange-500">
                            ⚠️ Accès prolongé : assurez-vous que cela est
                            nécessaire.
                          </p>
                        )}

                        <p className="text-xs text-muted-foreground">
                          Durée totale :{" "}
                          <span className="font-medium">
                            {durationInMinutes} minutes
                          </span>
                        </p>
                      </div>

                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                          disabled={isInvalidDuration}
                          onClick={() => {
                            postData({
                              url:
                                import.meta.env.VITE_API_ENDPOINT +
                                "/user/access/" +
                                user?.id,
                              data: {
                                durationInMinutes,
                              },
                            }).then((res) => {
                              if (res) {
                                toast({
                                  title: "Accès donné avec succès",
                                  description: `Durée : ${durationInMinutes} minutes`,
                                });
                              }
                            });
                          }}
                        >
                          Confirmer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <hr />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        disabled={
                          localStorage.getItem("isImpersonating") === "true"
                        }
                        className="flex cursor-pointer items-center gap-2 py-2 text-muted-foreground transition-all hover:text-destructive disabled:opacity-50"
                      >
                        <LogOut size={20} />
                        {t("Déconnexion")}
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t("Êtes-vous sûr de vouloir vous déconnecter ?")}
                        </AlertDialogTitle>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            sessionManager.logout();
                            logout(window.location.href);
                          }}
                        >
                          {t("Se déconnecter")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </PopoverContent>
              </Popover>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
};

export default Navigation;

